import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { api } from '@pilot-hub/api-client'
import { useEinsatz, useBestaetigung, useModal, useShellMode, aktuellePhase, phasenFuerTyp, phaseIndex, phaseLabel, hatPhasenleiste, offeneEinsatzChecklisten, type EinsatzPhaseKey } from '@pilot-hub/ui'
import { AlertTriangle, ChevronRight, Share2, X, Zap, Flag } from 'lucide-react'
import { FreigabeLinkDialog } from './FreigabeLinkDialog'
import { useFuehrungAktionen, useSchnellAktionenListe } from './schnellAktionen'

/** Ereignis, mit dem andere Seiten (Dashboard) nach einem Phasenwechsel von der Kopfzeile neu laden */
export const PHASE_GEAENDERT = 'fuehrung:phase-geaendert'

interface EinsatzInfo { id: number | string; typ?: string | null; status?: string | null; ergebnis?: string | null }

/**
 * Phasenwechsel mit derselben Logik wie die Zeitleiste im Dashboard:
 * weicher Hinweis statt Sperre; aktive Nachfrage bei EINSATZENDE mit offenen Checklisten und beim Abschluss ohne Ergebnis.
 */
export function usePhasenSetzen(opt: {
  einsatz: EinsatzInfo | null | undefined
  holeChecklisten: () => Promise<any[]>
  nachher?: () => Promise<unknown> | void
}) {
  const navigate = useNavigate()
  const { bestaetigen, dialog } = useBestaetigung()
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const optRef = useRef(opt); optRef.current = opt

  const waehle = useCallback(async (ziel: EinsatzPhaseKey): Promise<boolean> => {
    const { einsatz, holeChecklisten, nachher } = optRef.current
    if (!einsatz || busy) return false
    setFehler(null)
    const typ = einsatz.typ ?? undefined
    if (ziel === 'EINSATZENDE' && typ !== 'UEBUNG') {
      let liste: any[] = []
      try { liste = await holeChecklisten() } catch { /* ohne Liste: keine Nachfrage */ }
      const offen = offeneEinsatzChecklisten(liste, { typ, ohnePhase: 'EINSATZENDE' })
      if (offen.length) {
        const ok = await bestaetigen({
          titel: 'Einsatz wirklich beenden?',
          text: `Es ${offen.length === 1 ? 'ist noch 1 Checkliste' : `sind noch ${offen.length} Checklisten`} offen:`,
          hinweis: <ul className="list-disc pl-5">{offen.map(o => <li key={o.instanz.id}>{o.text}</li>)}</ul>,
          bestaetigenText: 'Trotzdem beenden', gefahr: true,
        })
        if (!ok) return false
      }
    }
    if (ziel === 'ABGESCHLOSSEN' && !einsatz.ergebnis) {
      const ok = await bestaetigen({
        titel: 'Einsatz ohne Ergebnis abschließen?',
        text: 'Für diesen Einsatz ist noch kein Ergebnis eingetragen (z. B. Gefunden, Fehlalarm).',
        hinweis: 'Das Ergebnis ist freiwillig. „Ergebnis jetzt eintragen“ führt direkt zur Bearbeitung dieses Einsatzes in der Einsatzverwaltung; auch nach dem Abschluss lässt es sich dort nachtragen.',
        bestaetigenText: 'Trotzdem abschließen', abbrechenText: 'Zurück', gefahr: false,
        weitereAktion: { text: 'Ergebnis jetzt eintragen', onClick: () => navigate(`/einsaetze?bearbeiten=${einsatz.id}`) },
      })
      if (!ok) return false
    }
    setBusy(true)
    try {
      await api.put(`/einsaetze/${einsatz.id}/phase`, { phase: ziel })
      await nachher?.()
      return true
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Phase konnte nicht gesetzt werden')
      return false
    } finally {
      setBusy(false)
    }
  }, [busy, bestaetigen, navigate])

  return { waehle, busy, fehler, setFehler, dialog }
}

function Sheet({ titel, onClose, children, testid }: { titel: string; onClose: () => void; children: ReactNode; testid?: string }) {
  const panel = useRef<HTMLDivElement>(null)
  useModal(true, panel, onClose)
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-end justify-center bg-black/60 sm:items-center" onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <div ref={panel} role="dialog" aria-modal="true" aria-label={titel} tabIndex={-1} data-testid={testid}
        className="max-h-[90dvh] w-full overflow-y-auto rounded-t-2xl border border-dark-border bg-dark-bg-secondary p-4 shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-dark-text">{titel}</h2>
          <button type="button" onClick={onClose} aria-label="Schließen" className="flex h-11 w-11 items-center justify-center rounded-lg text-dark-text-muted hover:bg-dark-bg-tertiary"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}

/** Kopfzeile (Layout/headerRight): Phasen-Chip mit Wechsel-Sheet, Schnellaktionen am Tablet, Aktionen für die Befehlspalette. */
export function PhasenSteuerung() {
  const { einsatz, reload } = useEinsatz()
  const { mode } = useShellMode()
  const [phaseOffen, setPhaseOffen] = useState(false)
  const [schnellOffen, setSchnellOffen] = useState(false)
  const [linkOffen, setLinkOffen] = useState(false)
  const [checklisten, setChecklisten] = useState<any[]>([])
  const schnell = useSchnellAktionenListe()

  const raw = (einsatz?.raw ?? {}) as Record<string, any>
  const info: EinsatzInfo | null = einsatz ? { id: einsatz.id, typ: einsatz.typ, status: einsatz.status, ergebnis: raw.ergebnis ?? null } : null
  const holeChecklisten = useCallback(async () => {
    if (!einsatz) return []
    const r = await api.get(`/einsaetze/${einsatz.id}/checklisten`)
    const liste = ((r.data as any[]) || []).filter(c => !c.flug_id)
    setChecklisten(liste)
    return liste
  }, [einsatz?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const { waehle, busy, fehler, setFehler, dialog } = usePhasenSetzen({
    einsatz: info, holeChecklisten,
    nachher: async () => { await reload(); window.dispatchEvent(new Event(PHASE_GEAENDERT)) },
  })

  const hat = !!einsatz && hatPhasenleiste(einsatz.typ)
  const aktuell = hat ? aktuellePhase({ typ: einsatz!.typ, status: einsatz!.status }) : null
  const phasen = phasenFuerTyp(einsatz?.typ)
  const idx = phaseIndex(aktuell, einsatz?.typ)
  const naechste = hat && idx >= 0 && idx < phasen.length - 1 ? phasen[idx + 1] : null

  // beim Öffnen und alle 20 s im offenen Sheet aktuelle Checklisten holen (weicher Hinweis)
  useEffect(() => {
    if (!phaseOffen) return
    void holeChecklisten().catch(() => {})
    const t = window.setInterval(() => { void holeChecklisten().catch(() => {}) }, 20000)
    return () => window.clearInterval(t)
  }, [phaseOffen, holeChecklisten])

  useFuehrungAktionen(einsatz ? [
    ...(naechste ? [{
      id: 'phase-weiter', label: `Phase weiter: ${naechste.label}`, group: 'Aktion', keywords: 'phase weiter nächste status einsatz',
      icon: <Flag className="h-4 w-4" />, run: () => setPhaseOffen(true),
    }] : []),
    { id: 'freigabelink', label: 'Freigabelink für Externe', group: 'Aktion', keywords: 'freigabe link qr extern leitstelle polizei teilen', icon: <Share2 className="h-4 w-4" />, run: () => setLinkOffen(true) },
  ] : [], [einsatz?.id, naechste?.key])

  if (!einsatz) return null
  const offene = aktuell && aktuell !== 'GEPLANT' && aktuell !== 'ABGESCHLOSSEN'
    ? offeneEinsatzChecklisten(checklisten, { typ: einsatz.typ, bisPhase: aktuell }) : []
  const naechsteOffene = naechste?.key === 'EINSATZENDE' ? offeneEinsatzChecklisten(checklisten, { typ: einsatz.typ, ohnePhase: 'EINSATZENDE' }) : []
  const hinweisListe = naechsteOffene.length ? naechsteOffene : offene

  return (
    <>
      {dialog}
      {hat && (
        <button type="button" onClick={() => setPhaseOffen(true)} data-testid="phasen-chip" title="Phase wechseln"
          aria-label={`Phase: ${phaseLabel(aktuell) || 'Phase'}`}
          className="inline-flex h-9 max-w-[11rem] shrink-0 items-center gap-1.5 rounded-full border border-primary-500/50 bg-primary-500/15 px-3 text-xs font-semibold text-dark-text hover:bg-primary-500/25">
          <Flag className="h-3.5 w-3.5 shrink-0 text-primary-400" />
          {mode === 'desktop' && <span className="truncate">{phaseLabel(aktuell) || 'Phase'}</span>}
        </button>
      )}
      {mode !== 'desktop' && (
        <button type="button" onClick={() => setSchnellOffen(true)} data-testid="schnellaktionen-knopf" aria-label="Schnellaktionen" title="Schnellaktionen"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-dark-border text-dark-text-muted hover:bg-dark-bg-tertiary hover:text-dark-text">
          <Zap className="h-4 w-4" />
        </button>
      )}

      {phaseOffen && (
        <Sheet titel="Phase" onClose={() => { setPhaseOffen(false); setFehler(null) }} testid="phasen-sheet">
          <p className="text-sm text-dark-text-muted">Aktuell: <b className="text-dark-text">{phaseLabel(aktuell) || '—'}</b></p>
          {hinweisListe.length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-200" role="status" data-testid="phasen-sheet-offen">
              <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4 shrink-0" />
                {hinweisListe.length === 1 ? 'Noch 1 Checkliste offen' : `Noch ${hinweisListe.length} Checklisten offen`}
              </p>
              <ul className="mt-1 list-disc pl-5">{hinweisListe.map(o => <li key={o.instanz.id}>{o.text}</li>)}</ul>
              <p className="mt-1 text-xs text-amber-200/80">Nur ein Hinweis – der Wechsel bleibt möglich.</p>
            </div>
          )}
          {fehler && <p className="mt-3 rounded bg-red-500/10 px-3 py-2 text-sm text-red-300" role="alert">{fehler}</p>}
          {naechste ? (
            <button type="button" disabled={busy} data-testid="phase-weiter"
              onClick={async () => { if (await waehle(naechste.key)) setPhaseOffen(false) }}
              className="mt-4 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 text-base font-semibold text-white hover:bg-primary-500 disabled:opacity-60">
              Weiter: {naechste.label}<ChevronRight className="h-5 w-5 shrink-0" />
            </button>
          ) : <p className="mt-4 text-sm text-dark-text-muted">Keine weitere Phase.</p>}
          <p className="mt-3 text-xs text-dark-text-subtle">Zurück oder Überspringen sowie die Zeiten: Phasen-Zeitleiste im Dashboard.</p>
        </Sheet>
      )}

      {schnellOffen && (
        <Sheet titel="Schnellaktionen" onClose={() => setSchnellOffen(false)} testid="schnellaktionen-sheet">
          <div className="grid gap-2">
            {schnell.map(a => (
              <button key={a.id} type="button" onClick={() => { setSchnellOffen(false); a.run() }}
                className="flex min-h-[52px] items-center gap-3 rounded-xl border border-dark-border bg-dark-bg-tertiary/50 px-3 text-left text-sm font-medium text-dark-text hover:bg-dark-bg-tertiary">
                <span className="text-primary-400">{a.icon}</span>{a.label}
              </button>
            ))}
            {schnell.length === 0 && <p className="text-sm text-dark-text-muted">Keine Aktionen verfügbar.</p>}
          </div>
        </Sheet>
      )}

      {linkOffen && <FreigabeLinkDialog einsatzId={String(einsatz.id)} onSchliessen={() => setLinkOffen(false)} />}
    </>
  )
}
